import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (userData) => api.post("/auth/register", userData),
  getMe: () => api.get("/auth/me"),
};

// Users API
export const usersAPI = {
  getAll: () => api.get("/users"),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Document Types API
export const documentTypesAPI = {
  getAll: () => api.get("/document-types"),
  create: (data) => api.post("/document-types", data),
  update: (id, data) => api.put(`/document-types/${id}`, data),
  delete: (typeId) => api.delete(`/document-types/${typeId}`),
};

// Protocols API
export const protocolsAPI = {
  getAll: (params) => api.get("/protocols", { params }),
  getOne: (id) => api.get(`/protocols/${id}`),
  create: (data) => api.post("/protocols", data),
  update: (id, data) => api.put(`/protocols/${id}`, data),
  delete: (id) => api.delete(`/protocols/${id}`),
  previewNumber: (params) => api.get("/protocols/preview-number", { params }),
};

// Stats API
export const statsAPI = {
  get: () => api.get("/stats"),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get("/notifications", { params }),
  getCount: () => api.get("/notifications/count"),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put("/notifications/read-all"),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Audit Log API
export const auditLogAPI = {
  getAll: (params) => api.get("/audit-logs", { params }),
};

// Import API
export const importAPI = {
  preview: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/import/preview", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  execute: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/import/execute", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  downloadTemplate: () => api.get("/import/template", { responseType: "blob" }),
};

// OCR API
export const ocrAPI = {
  extract: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/ocr/extract", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Email Account API
export const emailAccountsAPI = {
  getAll: () => api.get("/email-accounts"),
  create: (data) => api.post("/email-accounts", data),
  update: (id, data) => api.put(`/email-accounts/${id}`, data),
  delete: (id) => api.delete(`/email-accounts/${id}`),
  sync: (id) => api.post(`/email-accounts/${id}/sync`),
};

// Email Queue API
export const emailQueueAPI = {
  getAll: (params) => api.get("/email-queue", { params }),
  approve: (id) => api.put(`/email-queue/${id}/approve`),
  reject: (id) => api.put(`/email-queue/${id}/reject`),
  markSpam: (id) => api.put(`/email-queue/${id}/spam`),
  markProcessed: (id, protocolNumber) => api.put(`/email-queue/${id}/mark-processed`, null, { params: { protocol_number: protocolNumber } }),
  delete: (id) => api.delete(`/email-queue/${id}`),
  clearAll: () => api.post("/email-queue/clear-all"),
  getFull: (id) => api.get(`/email-queue/${id}/full`),
  downloadAttachment: (emailId, attachmentIndex) => 
    api.get(`/email-queue/${emailId}/attachment/${attachmentIndex}`, { responseType: "blob" }),
  restore: (id) => api.put(`/email-queue/${id}/restore`),
};

// SMTP Settings API
export const smtpAPI = {
  get: () => api.get("/smtp-settings"),
  save: (data) => api.post("/smtp-settings", data),
  test: (data) => api.post("/smtp-settings/test", data),
  delete: () => api.delete("/smtp-settings"),
};

// Export API
export const exportAPI = {
  excel: (params) => api.get("/export/excel", { params, responseType: "blob" }),
  pdf: (params) => api.get("/export/pdf", { params, responseType: "blob" }),
};

// Backup API
export const backupAPI = {
  getAll: () => api.get("/backups"),
  create: () => api.post("/backups"),
  download: (backupId) => api.get(`/backups/${backupId}/download`, { responseType: "blob" }),
  restore: (backupId) => api.post(`/backups/restore/${backupId}`),
  delete: (backupId) => api.delete(`/backups/${backupId}`),
  getSettings: () => api.get("/backup-settings"),
  updateSettings: (data) => api.put("/backup-settings", data),
};

// Contacts Registry API
export const contactsAPI = {
  getAll: (params) => api.get("/contacts", { params }),
  getOne: (id) => api.get(`/contacts/${id}`),
  getProtocols: (id) => api.get(`/contacts/${id}/protocols`),
  create: (data) => api.post("/contacts", data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
};

// Internal Contacts API (users without login)
export const internalContactsAPI = {
  getAll: () => api.get("/internal-contacts"),
  create: (data) => api.post("/internal-contacts", data),
  update: (id, data) => api.put(`/internal-contacts/${id}`, data),
  delete: (id) => api.delete(`/internal-contacts/${id}`),
};

// Locations API
export const locationsAPI = {
  getAll: () => api.get("/locations"),
  create: (data) => api.post("/locations", data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
};

// Assignees API (combined users + internal contacts)
export const assigneesAPI = {
  getAll: () => api.get("/assignees"),
};

// Global Search API
export const searchAPI = {
  search: (q) => api.get("/search", { params: { q } }),
};

// OneDrive API
export const onedriveAPI = {
  getStatus: () => api.get("/onedrive/status"),
  authorize: () => api.get("/onedrive/authorize"),
  disconnect: () => api.post("/onedrive/disconnect"),
  testConnection: () => api.get("/onedrive/test"),
  listFolders: (path = "") => api.get("/onedrive/folders", { params: { path } }),
  listItems: (path = "") => api.get("/onedrive/items", { params: { path } }),
  createFolder: (parent_path, name) => api.post("/onedrive/folders", { parent_path, name }),
  getAllRootFolders: () => api.get("/onedrive/all-root-folders"),
  getAllowedFolders: () => api.get("/onedrive/allowed-folders"),
  setAllowedFolders: (allowed_folders) => api.put("/onedrive/allowed-folders", { allowed_folders }),
  getAttachmentContent: (protocolId, attIndex) =>
    api.get(`/protocols/${protocolId}/attachments/${attIndex}/content`, { responseType: "blob" }),
  getAttachmentDownloadUrl: (protocolId, attIndex) =>
    api.get(`/protocols/${protocolId}/attachments/${attIndex}/download-url`),
};

export default api;
